import clsx from "clsx";
import { CircleCheck, OctagonAlert, TriangleAlert } from "lucide-react";

import type { CurvePhase, LogisticsMaintenance, MaintenanceColor, MaintenanceFamily, MaintenancePhase, MaterielFamily } from "../../api/types";
import { Badge } from "../../components/ui";
import { formatNumber, k, t } from "../../i18n/i18n";
import { colorTone, CURVE_PHASES, dtoColor, maintenanceColor, percentText, REQUIRED_PHASES } from "../../maintenance";

/** Pièces de la vue Maintenance (chantier « DTO / CAPOPS », DTO requise par parc) : icônes d'état, l'une pour la DTO
 *  (jugée contre la DTO requise de son parc à chaque temps), l'autre pour la CAPOPS (seuils de l'iPad), jamais
 *  confondues ; courbe de T0 à T3 avec les paliers de la DTO requise ; chute de DTO de chaque temps. La couleur va
 *  toujours avec une icône de forme différente et un texte : jamais la couleur seule. */

const num = (value: number, digits = 1) => formatNumber(value, { minimumFractionDigits: digits, maximumFractionDigits: digits });

const ICONS: Record<MaintenanceColor, typeof CircleCheck> = { green: CircleCheck, orange: TriangleAlert, red: OctagonAlert };

/** Lecture d'une DTO, au survol et pour les lecteurs d'écran. */
export const DTO_STATUS: Record<MaintenanceColor, string> = {
  green: k("Conforme à la DTO requise"),
  orange: k("Sous la DTO requise de moins de 5 points"),
  red: k("Sous la DTO requise de 5 points ou plus"),
};

/** Lecture d'une CAPOPS (seuils de l'iPad). */
export const CAPOPS_STATUS: Record<MaintenanceColor, string> = {
  green: k("Au-dessus de 80 %"),
  orange: k("De 61 à 80 %"),
  red: k("60 % ou moins"),
};

export const TONE_TEXT = { ok: "text-ok", warn: "text-warn", danger: "text-danger", neutral: "text-ink-muted" } as const;

function StateIcon({ color, text, size }: { color: MaintenanceColor; text: string; size: number }) {
  const Icon = ICONS[color];
  return (
    <span role="img" aria-label={t(text)} title={t(text)} className={clsx("flex shrink-0", TONE_TEXT[colorTone(color)])}>
      <Icon size={size} aria-hidden />
    </span>
  );
}

/** Icône d'état d'une DTO face à la DTO `required` requise de son temps ; rien sans requise (T0, DTO de départ). */
export function DtoStatusIcon({ percent, required, size = 14 }: { percent: number; required: number | null; size?: number }) {
  const color = dtoColor(percent, required);
  if (!color) return <span aria-hidden className="flex shrink-0" style={{ width: size, height: size }} />;
  return <StateIcon color={color} text={DTO_STATUS[color]} size={size} />;
}

/** Icône d'état d'une CAPOPS (vert > 80 %, orange 61 à 80 %, rouge ≤ 60 %). */
export function CapopsStatusIcon({ percent, size = 14 }: { percent: number; size?: number }) {
  const color = maintenanceColor(percent) ?? "red";
  return <StateIcon color={color} text={CAPOPS_STATUS[color]} size={size} />;
}

/** DTO en badge coloré face à la DTO requise, précédée de son icône d'état ; neutre sans requise (T0). */
export function DtoBadge({ percent, required }: { percent: number; required: number | null }) {
  const color = dtoColor(percent, required);
  if (!color) return <Badge>{percentText(percent)}</Badge>;
  const Icon = ICONS[color];
  return (
    <Badge tone={colorTone(color)} title={t(DTO_STATUS[color])}>
      <Icon size={12} aria-hidden className="mr-1" />
      {percentText(percent)}
    </Badge>
  );
}

/** CAPOPS en badge coloré (seuils de l'iPad), précédée de son icône d'état. `title` : info-bulle propre (temps et ce
 *  qui limite l'unité, tableau des CAPOPS), sinon la lecture du seuil. */
export function CapopsBadge({ percent, title }: { percent: number; title?: string }) {
  const color = maintenanceColor(percent) ?? "red";
  const Icon = ICONS[color];
  return (
    <Badge tone={colorTone(color)} title={title ?? t(CAPOPS_STATUS[color])}>
      <Icon size={12} aria-hidden className="mr-1" />
      {percentText(percent)}
    </Badge>
  );
}

/** Courbe de T0 à T3 d'un parc, sobre : paliers en tirets de la DTO requise de T1 à T3 (nommés et chiffrés par la
 *  légende sous le dessin), trait de la DTO, points colorés face à la requise avec leur valeur (T0, DTO de départ,
 *  neutre), temps affiché en gras ; ni grille ni axe gradué (les valeurs sont écrites). `dto` et `required` peuvent
 *  être ceux d'un curseur en cours de réglage (aperçu immédiat). Échelle de 40 points au moins, de la dizaine autour
 *  des valeurs et des paliers. */
export function DtoCurve({
  dto,
  required,
  phase,
}: {
  dto: Record<CurvePhase, number>;
  required: Record<MaintenancePhase, number>;
  phase: CurvePhase;
}) {
  const W = 320;
  const H = 112;
  const left = 22;
  const right = 22;
  const top = 18;
  const bottom = 22;
  const values = CURVE_PHASES.map((p) => dto[p]);
  const needs = REQUIRED_PHASES.map((p) => required[p]);
  const high = Math.min(100, Math.ceil((Math.max(...values, ...needs) + 5) / 10) * 10);
  const low = Math.max(0, Math.min(high - 40, Math.floor((Math.min(...values, ...needs) - 5) / 10) * 10));
  const y = (v: number) => top + ((high - v) / (high - low)) * (H - top - bottom);
  const x = (i: number) => left + (i * (W - left - right)) / (CURVE_PHASES.length - 1);
  const step = (W - left - right) / (CURVE_PHASES.length - 1);
  const points = values.map((v, i) => `${x(i)},${y(v)}`).join(" ");
  // Paliers : un trait par temps, centré sur son point, relié au suivant par un ressaut.
  const stairs = REQUIRED_PHASES.flatMap((p, j) => {
    const i = j + 1; // T1 est le deuxième point de la courbe
    const from = Math.max(left, x(i) - step / 2);
    const to = Math.min(W - right, x(i) + step / 2);
    return [`${from},${y(required[p])}`, `${to},${y(required[p])}`];
  }).join(" ");
  const label = t("Évolution de T0 à T3 : {values} ; DTO requise : {required}", {
    values: CURVE_PHASES.map((p) => `${p} ${percentText(dto[p])}`).join(", "),
    required: REQUIRED_PHASES.map((p) => `${p} ${percentText(required[p])}`).join(", "),
  });
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={label} className="block h-auto w-full overflow-visible text-meta">
        {/* DTO requise : paliers en tirets, nommés et chiffrés par la légende. */}
        <polyline
          points={stairs}
          fill="none"
          stroke="currentColor"
          strokeWidth={1.25}
          strokeDasharray="4 3"
          className="text-ink-muted"
        />
        {/* Courbe */}
        <polyline points={points} fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinejoin="round" className="text-ink-soft" />
        {CURVE_PHASES.map((p, i) => {
          const v = dto[p];
          const need = p === "T0" ? null : required[p];
          const tone = TONE_TEXT[colorTone(dtoColor(v, need))];
          const current = p === phase;
          return (
            <g key={p}>
              <circle cx={x(i)} cy={y(v)} r={current ? 4.5 : 3.25} fill="currentColor" className={clsx(tone, "transition-colors")} stroke="var(--color-surface)" strokeWidth={1.5} />
              <text x={x(i)} y={y(v) - 8} textAnchor="middle" className={clsx("fill-ink text-caps tabular-nums", current && "font-semibold")}>
                {percentText(v)}
              </text>
              <text x={x(i)} y={H - 5} textAnchor="middle" className={clsx("text-caps", current ? "fill-ink font-semibold" : "fill-ink-muted")}>
                {p}
              </text>
            </g>
          );
        })}
      </svg>
      <p className="mt-1 flex items-center gap-1.5 text-meta leading-snug text-ink-muted">
        <svg aria-hidden width="16" height="6" className="shrink-0 text-ink-muted">
          <line x1="0" y1="3" x2="16" y2="3" stroke="currentColor" strokeWidth="1.25" strokeDasharray="4 3" />
        </svg>
        {t("DTO requise : {values}", { values: REQUIRED_PHASES.map((p) => `${p} ${percentText(required[p])}`).join(", ") })}
      </p>
    </div>
  );
}

/** Chute de DTO, lisible : la chute nette de T0 à T3 d'abord, en points et en véhicules du parc, puis, temps par
 *  temps, la chute nette du temps et sa décomposition (avaries et dommages de combat, dont pertes définitives ;
 *  remises en état), puis les avaries et pertes jouées en DIREX. Jamais un cumul sans le dire : le cumul des avaries
 *  d'une longue opération peut dépasser le parc, les matériels remis en état pouvant retomber en avarie. */
export function LegLines({ family, hours }: { family: MaintenanceFamily; hours: LogisticsMaintenance["hours"] }) {
  const lines = REQUIRED_PHASES.flatMap((p) => {
    const failures = family.failures[p] ?? 0;
    const repairs = family.repairs[p] ?? 0;
    const losses = family.losses[p] ?? 0;
    if (failures < 0.05 && repairs < 0.05) return [];
    const values = {
      phase: p,
      hours: formatNumber(Math.round(hours[p] ?? 0)),
      net: num(Math.max(0, failures - repairs)),
      failures: num(failures),
      losses: num(losses),
      repairs: num(repairs),
    };
    return [
      losses >= 0.05
        ? t("{phase} ({hours} h) : −{net} ; avaries et dommages de combat −{failures}, dont pertes définitives −{losses} ; remises en état +{repairs}.", values)
        : t("{phase} ({hours} h) : −{net} ; avaries et dommages de combat −{failures} ; remises en état +{repairs}.", values),
    ];
  });
  const drops = CURVE_PHASES.flatMap((p) => {
    const n = family.drops[p];
    return n ? [t("Avaries et pertes jouées en DIREX à {phase} : −{n}.", { n: num(n), phase: p })] : [];
  });
  if (!lines.length && !drops.length) return <p className="text-meta leading-snug text-ink-muted">{t("Aucune indisponibilité de T0 à T3.")}</p>;
  const net = Math.max(0, Math.round(family.dto.T0) - Math.round(family.dto.T3));
  const cars = Math.round((Math.max(0, family.dto.T0 - family.dto.T3) * family.vehicles) / 100);
  return (
    <div className="space-y-1">
      <p className="text-meta leading-snug font-medium text-ink">
        {t("Chute nette de T0 à T3 : {n} points, environ {cars} véhicules sur {fleet}.", {
          n: formatNumber(net),
          cars: formatNumber(cars),
          fleet: formatNumber(Math.round(family.vehicles)),
        })}
      </p>
      <ul className="space-y-0.5 text-meta leading-snug text-ink-soft">
        {[...lines, ...drops].map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      <p className="text-meta leading-snug text-ink-muted">{t("En points de DTO du parc.")}</p>
    </div>
  );
}

/** Famille montrée par la courbe : la famille désignée, sinon la plus en retard au temps affiché, sinon la première. */
export function initialFamily(m: LogisticsMaintenance, focus: MaterielFamily | null): MaterielFamily | null {
  if (focus && m.families.some((f) => f.family === focus)) return focus;
  return m.lowest?.family ?? m.families[0]?.family ?? null;
}
